"use client";

import { LocaleLink } from "@i18n/routing";
import { useSession } from "@saas/auth/hooks/use-session";
import { creditsQueryKey } from "@saas/credits/hooks/use-credits";
import { CreditsPaywallModal } from "@saas/payments/components/CreditsPaywallModal";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import {
	GENERATION_HISTORY_QUERY_KEY,
	formatHistoryDate,
} from "@saas/shared/history/types";
import { apiClient } from "@shared/lib/api-client";
import { reportGenerationFailure } from "@shared/lib/report-generation-failure";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	DownloadIcon,
	FilmIcon,
	Loader2Icon,
	MicVocalIcon,
	MusicIcon,
	RotateCcwIcon,
	Trash2Icon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	MUSIC_HISTORY_PAGE_SIZE,
	MUSIC_POLL_INTERVAL_MS,
	MUSIC_VISUALIZER_COST,
	formatDuration,
} from "../config";
import type {
	MusicFormValues,
	MusicHistoryItem,
	MusicTrack,
	MusicVideoItem,
} from "../types";

export const MUSIC_HISTORY_QUERY_KEY = [
	...GENERATION_HISTORY_QUERY_KEY,
	MUSIC_HISTORY_PAGE_SIZE,
	"music",
];

const isInFlight = (status: string) =>
	status === "PENDING" || status === "PROCESSING";

/** 已收尾（上报失败、刷新列表和积分）过的行，模块级：卡片重挂载也不重复做 */
const settledRows = new Set<string>();

function proxyDownload(url: string) {
	window.location.href = `/api/proxy-download?url=${encodeURIComponent(url)}`;
}

/** Suno 自己拒版权歌词 / 艺人名时 failMsg 里会带这些词 */
export function musicFailureHint(
	message: string | null | undefined,
	t: (key: string) => string,
) {
	return /artist|copyright|lyrics|sensitive|prohibit/i.test(message ?? "")
		? t("workbench.copyrightHint")
		: t("feed.failed");
}

export function MusicFeed({
	onReuse,
	emptyState,
}: {
	onReuse: (values: MusicFormValues) => void;
	/** 访客 / 还没出过歌时右栏放的内容（样片）；没有就显示空状态提示 */
	emptyState?: ReactNode;
}) {
	const t = useTranslations("aiMusic");
	const { user } = useSession();
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useInfiniteQuery({
			queryKey: MUSIC_HISTORY_QUERY_KEY,
			enabled: Boolean(user),
			initialPageParam: 1,
			queryFn: async ({ pageParam }) => {
				const response = await apiClient.generations.history.$get({
					query: {
						page: String(pageParam),
						limit: String(MUSIC_HISTORY_PAGE_SIZE),
						filter: "music",
					},
				});
				if (!response.ok) {
					throw new Error("Failed to fetch history");
				}
				return response.json();
			},
			getNextPageParam: (last) =>
				last.pagination.page < last.pagination.totalPages
					? last.pagination.page + 1
					: undefined,
		});
	const items =
		data?.pages.flatMap(
			(page) => page.data as unknown as MusicHistoryItem[],
		) ?? [];

	if (!user || (!isLoading && items.length === 0)) {
		return (
			<div className="space-y-6">
				<div className="flex min-h-[160px] flex-col items-center justify-center gap-4 rounded-2xl bg-background p-8 text-center text-muted-foreground">
					<MusicIcon className="size-10 opacity-40" aria-hidden />
					<p className="text-sm">{t("feed.empty")}</p>
				</div>
				{emptyState}
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<h2 className="text-sm font-medium text-muted-foreground">
				{t("feed.title")}
			</h2>
			{isLoading && (
				<Loader2Icon className="mx-auto size-6 animate-spin text-muted-foreground" />
			)}
			{items.map((item) => (
				<MusicGenerationCard
					key={item.id}
					item={item}
					onReuse={onReuse}
				/>
			))}
			{hasNextPage && (
				<Button
					variant="outline"
					className="w-full"
					disabled={isFetchingNextPage}
					onClick={() => fetchNextPage()}
				>
					{t("feed.loadMore")}
				</Button>
			)}
		</div>
	);
}

function MusicGenerationCard({
	item,
	onReuse,
}: {
	item: MusicHistoryItem;
	onReuse: (values: MusicFormValues) => void;
}) {
	const t = useTranslations("aiMusic");
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationAlert();

	// 在途的行自己轮询，结束后刷新列表和积分
	const { data: status } = useQuery({
		queryKey: ["music-status", item.id],
		enabled: isInFlight(item.status),
		refetchInterval: (query) =>
			query.state.data && !isInFlight(query.state.data.status)
				? false
				: MUSIC_POLL_INTERVAL_MS,
		queryFn: async () => {
			const response = await apiClient.music.status[":id"].$get({
				param: { id: item.id },
			});
			if (!response.ok) {
				throw new Error("Failed to get music status");
			}
			return response.json();
		},
	});
	useEffect(() => {
		// 状态缓存会留 5 分钟，卡片在音乐页和 My Assets 之间重挂载时 effect 会再跑：每行只收尾一次
		if (!status || isInFlight(status.status) || settledRows.has(item.id)) {
			return;
		}
		settledRows.add(item.id);
		if (status.status === "FAILED") {
			reportGenerationFailure("music", item.model, status.errorMessage);
		}
		queryClient.invalidateQueries({ queryKey: MUSIC_HISTORY_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: creditsQueryKey });
	}, [status, item.id, item.model, queryClient]);

	const handleDelete = () => {
		if (isInFlight(item.status)) {
			toast.info(t("feed.deleteInProgress"));
			return;
		}
		confirm({
			title: t("feed.deleteConfirmTitle"),
			message: t("feed.deleteConfirmMessage"),
			confirmLabel: t("feed.delete"),
			destructive: true,
			onConfirm: async () => {
				const response = await apiClient.music[":id"].$delete({
					param: { id: item.id },
				});
				if (response.status === 409) {
					toast.info(t("feed.deleteInProgress"));
					return;
				}
				if (!response.ok) {
					toast.error(t("feed.deleteFailed"));
					return;
				}
				queryClient.invalidateQueries({
					queryKey: MUSIC_HISTORY_QUERY_KEY,
				});
			},
		});
	};

	const heading =
		item.title || item.prompt || item.style || t("feed.untitled");

	return (
		<article className="rounded-2xl bg-background p-4 shadow-sm">
			<header className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate font-medium text-sm">{heading}</p>
					<p className="mt-0.5 text-muted-foreground text-xs">
						{formatHistoryDate(item.createdAt)}
						{item.style && item.title ? ` · ${item.style}` : ""}
					</p>
				</div>
				<div className="flex shrink-0 gap-1">
					<Button
						variant="ghost"
						size="icon"
						aria-label={t("feed.reuse")}
						title={t("feed.reuse")}
						onClick={() => onReuse(item)}
					>
						<RotateCcwIcon className="size-4" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						aria-label={t("feed.delete")}
						title={t("feed.delete")}
						onClick={handleDelete}
					>
						<Trash2Icon className="size-4" />
					</Button>
				</div>
			</header>

			{isInFlight(item.status) && (
				<div className="mt-4 flex items-center gap-3 rounded-xl bg-zinc-50 p-4 text-muted-foreground text-sm dark:bg-muted/40">
					<Loader2Icon className="size-4 animate-spin" />
					{t("feed.generating")}
				</div>
			)}
			{item.status === "FAILED" && (
				<p className="mt-4 rounded-xl bg-red-50 p-3 text-red-700 text-sm dark:bg-red-950/40 dark:text-red-300">
					{musicFailureHint(item.errorMessage, t)}
				</p>
			)}
			{item.status === "SUCCESS" && (
				<div className="mt-4 space-y-3">
					{(item.tracks ?? []).map((track) => (
						<MusicTrackRow
							key={track.id}
							generationId={item.id}
							track={track}
							video={item.videos.find(
								(video) =>
									video.trackId === track.id &&
									video.status !== "FAILED",
							)}
						/>
					))}
				</div>
			)}
		</article>
	);
}

function MusicTrackRow({
	generationId,
	track,
	video,
}: {
	generationId: string;
	track: MusicTrack;
	video?: MusicVideoItem;
}) {
	const t = useTranslations("aiMusic");
	const queryClient = useQueryClient();
	const { canRemoveWatermark: isPaid } = usePurchases();
	const [paywall, setPaywall] = useState<
		"music-wav" | "insufficient-credits" | null
	>(null);
	const [wavPending, setWavPending] = useState(false);
	const [videoTaskId, setVideoTaskId] = useState<string | null>(
		video && isInFlight(video.status) ? video.id : null,
	);
	const [startingVideo, setStartingVideo] = useState(false);
	const [videoFailed, setVideoFailed] = useState(false);
	const [videoUrl, setVideoUrl] = useState<string | null>(
		video?.videoUrl ?? null,
	);
	// 视频可能是 cron 或另一个标签页收尾的：列表刷新带来新的 video 时跟上
	const videoId = video?.id;
	const videoStatusProp = video?.status;
	const videoUrlProp = video?.videoUrl;
	useEffect(() => {
		if (videoUrlProp) {
			setVideoUrl(videoUrlProp);
			setVideoTaskId(null);
		} else if (videoId && videoStatusProp && isInFlight(videoStatusProp)) {
			setVideoTaskId((current) => current ?? videoId);
		}
	}, [videoId, videoStatusProp, videoUrlProp]);

	const { data: videoStatus } = useQuery({
		queryKey: ["music-visualizer", videoTaskId],
		enabled: Boolean(videoTaskId),
		refetchInterval: (query) =>
			query.state.data && !isInFlight(query.state.data.status)
				? false
				: MUSIC_POLL_INTERVAL_MS,
		queryFn: async () => {
			const response = await apiClient.music.visualizer[":id"].$get({
				param: { id: videoTaskId as string },
			});
			if (!response.ok) {
				throw new Error("Failed to get visualizer status");
			}
			return response.json();
		},
	});
	useEffect(() => {
		if (!videoStatus || isInFlight(videoStatus.status)) {
			return;
		}
		setVideoTaskId(null);
		if (videoStatus.status === "SUCCESS" && videoStatus.videoUrl) {
			setVideoUrl(videoStatus.videoUrl);
		} else {
			setVideoFailed(true);
		}
		queryClient.invalidateQueries({ queryKey: creditsQueryKey });
		queryClient.invalidateQueries({ queryKey: MUSIC_HISTORY_QUERY_KEY });
	}, [videoStatus, queryClient]);

	const startVideo = async () => {
		setStartingVideo(true);
		setVideoFailed(false);
		try {
			const response = await apiClient.music[":id"].tracks[
				":trackId"
			].visualizer.$post({
				param: { id: generationId, trackId: track.id },
			});
			if (response.status === 402) {
				setPaywall("insufficient-credits");
				return;
			}
			if (!response.ok) {
				throw new Error("Failed to start visualizer");
			}
			const result = await response.json();
			if (result.status === "SUCCESS" && result.videoUrl) {
				setVideoUrl(result.videoUrl);
			} else if (result.status === "FAILED") {
				setVideoFailed(true);
			} else {
				setVideoTaskId(result.id);
			}
		} catch (error) {
			console.error(error);
			setVideoFailed(true);
		} finally {
			setStartingVideo(false);
		}
	};

	// WAV 是异步转换：反复调同一个接口直到拿到地址
	const downloadWav = async () => {
		if (!isPaid) {
			setPaywall("music-wav");
			return;
		}
		if (track.wavUrl) {
			proxyDownload(track.wavUrl);
			return;
		}
		setWavPending(true);
		try {
			for (let attempt = 0; attempt < 40; attempt++) {
				const response = await apiClient.music[":id"].tracks[
					":trackId"
				].wav.$post({ param: { id: generationId, trackId: track.id } });
				if (response.status === 403) {
					setPaywall("music-wav");
					return;
				}
				if (!response.ok) {
					throw new Error("Failed to prepare WAV");
				}
				const result = await response.json();
				if (result.status === "SUCCESS" && result.wavUrl) {
					proxyDownload(result.wavUrl);
					queryClient.invalidateQueries({
						queryKey: MUSIC_HISTORY_QUERY_KEY,
					});
					return;
				}
				if (result.status === "FAILED") {
					throw new Error("WAV conversion failed");
				}
				await new Promise((resolve) =>
					setTimeout(resolve, MUSIC_POLL_INTERVAL_MS),
				);
			}
			throw new Error("WAV conversion timed out");
		} catch (error) {
			console.error(error);
			toast.error(t("feed.wavFailed"));
		} finally {
			setWavPending(false);
		}
	};

	const makingVideo = startingVideo || Boolean(videoTaskId);

	return (
		<div className="rounded-xl border border-zinc-200 p-3 dark:border-border">
			<div className="flex items-center gap-3">
				{track.imageUrl ? (
					<img
						src={track.imageUrl}
						alt=""
						className="size-14 shrink-0 rounded-lg object-cover"
						loading="lazy"
					/>
				) : (
					<div className="flex size-14 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-muted">
						<MusicIcon className="size-5 text-muted-foreground" />
					</div>
				)}
				<div className="min-w-0 flex-1">
					<p className="truncate font-medium text-sm">
						{track.title}
					</p>
					<p className="text-muted-foreground text-xs">
						{formatDuration(track.duration)}
					</p>
				</div>
			</div>
			{/* biome-ignore lint/a11y/useMediaCaption: AI 生成的歌没有字幕轨 */}
			<audio
				controls
				preload="none"
				src={track.audioUrl}
				className="mt-3 h-10 w-full"
			/>
			{videoUrl && (
				// biome-ignore lint/a11y/useMediaCaption: 可视化视频只有歌曲音轨
				<video
					controls
					preload="metadata"
					src={videoUrl}
					className="mt-3 max-h-80 w-auto rounded-lg"
				/>
			)}
			<div className="mt-3 flex flex-wrap gap-2">
				<Button
					size="sm"
					variant="outline"
					onClick={() => proxyDownload(track.audioUrl)}
				>
					<DownloadIcon className="mr-1.5 size-3.5" />
					{t("feed.mp3")}
				</Button>
				<Button
					size="sm"
					variant="outline"
					disabled={wavPending}
					onClick={downloadWav}
				>
					{wavPending ? (
						<Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
					) : (
						<DownloadIcon className="mr-1.5 size-3.5" />
					)}
					{wavPending ? t("feed.preparingWav") : t("feed.wav")}
				</Button>
				{videoUrl ? (
					<Button
						size="sm"
						variant="outline"
						onClick={() => proxyDownload(videoUrl)}
					>
						<DownloadIcon className="mr-1.5 size-3.5" />
						{t("feed.downloadVideo")}
					</Button>
				) : (
					<Button
						size="sm"
						variant="outline"
						disabled={makingVideo}
						onClick={startVideo}
					>
						{makingVideo ? (
							<Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
						) : (
							<FilmIcon className="mr-1.5 size-3.5" />
						)}
						{makingVideo
							? t("feed.visualizerMaking")
							: t("feed.visualizer", {
									credits: MUSIC_VISUALIZER_COST,
								})}
					</Button>
				)}
				{/* 带着这首歌去对口型 MV 页（11/04 方案）：那边读 ?music=&track= 自动放进歌那一格 */}
				<Button size="sm" variant="outline" asChild>
					<LocaleLink
						href={`/ai-music-video-generator?music=${generationId}&track=${track.id}`}
					>
						<MicVocalIcon className="mr-1.5 size-3.5" />
						{t("feed.musicVideo")}
					</LocaleLink>
				</Button>
			</div>
			{videoFailed && (
				<p className={cn("mt-2 text-red-600 text-xs")}>
					{t("feed.visualizerFailed")}
				</p>
			)}
			<CreditsPaywallModal
				open={paywall !== null}
				onOpenChange={(open) => !open && setPaywall(null)}
				reason={paywall}
			/>
		</div>
	);
}
